import React from "react";
import { motion } from "framer-motion";

// Stylized "charging bull" silhouette in SVG with gold/sky gradients + glow.
// Designed front-facing with curved horns — premium, abstract, finance-iconic.
export default function BullMascot({ size = 460 }) {
  return (
    <motion.div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
      animate={{ y: [0, -18, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      data-testid="bull-mascot"
    >
      {/* Background glow halos */}
      <div className="absolute inset-0 rounded-full bg-sky-500/25 blur-[110px]" />
      <div className="absolute inset-10 rounded-full bg-[#D4AF37]/20 blur-[90px]" />
      <div className="absolute inset-20 rounded-full bg-sky-400/30 blur-[70px] animate-pulse-glow" />

      {/* Subtle rotation wrapper */}
      <motion.div
        className="relative"
        animate={{ rotate: [-2, 2, -2] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 500 500" width={size} height={size} className="drop-shadow-[0_20px_60px_rgba(14,165,233,0.45)]">
          <defs>
            {/* Sky gradient (main face) */}
            <linearGradient id="bull-face" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#7DD3FC" />
              <stop offset="55%" stopColor="#0EA5E9" />
              <stop offset="100%" stopColor="#0A1128" />
            </linearGradient>
            {/* Gold for horns / accents */}
            <linearGradient id="bull-gold" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F5D27A" />
              <stop offset="55%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#8C6F1F" />
            </linearGradient>
            {/* Sheen highlight */}
            <linearGradient id="bull-sheen" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fff" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="eye-glow">
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="60%" stopColor="#0EA5E9" />
              <stop offset="100%" stopColor="#0369A1" />
            </radialGradient>
            <filter id="soft-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* HORNS — large sweeping curves left and right */}
          <path
            d="M 165 180 C 95 110 50 60 30 145 C 75 130 110 160 155 200 Z"
            fill="url(#bull-gold)"
            stroke="#F5D27A"
            strokeWidth="1.5"
            filter="url(#soft-glow)"
          />
          <path
            d="M 335 180 C 405 110 450 60 470 145 C 425 130 390 160 345 200 Z"
            fill="url(#bull-gold)"
            stroke="#F5D27A"
            strokeWidth="1.5"
            filter="url(#soft-glow)"
          />

          {/* HEAD — bold geometric face */}
          <path
            d="M 145 175 C 145 130 200 95 250 95 C 300 95 355 130 355 175 L 348 295 C 348 345 305 385 250 385 C 195 385 152 345 152 295 Z"
            fill="url(#bull-face)"
            stroke="#0EA5E9"
            strokeWidth="2"
          />

          {/* Inner face plate (depth) */}
          <path
            d="M 175 200 C 180 175 215 158 250 158 C 285 158 320 175 325 200 L 320 280 C 315 305 290 320 250 320 C 210 320 185 305 180 280 Z"
            fill="#0A1128"
            opacity="0.45"
          />

          {/* Sheen on left side */}
          <path
            d="M 152 195 C 158 165 195 140 235 140 L 230 240 C 200 240 175 235 165 220 Z"
            fill="url(#bull-sheen)"
            opacity="0.6"
          />

          {/* MUZZLE */}
          <ellipse cx="250" cy="345" rx="78" ry="42" fill="#0A1128" stroke="#0EA5E9" strokeWidth="1.5" />
          <ellipse cx="250" cy="338" rx="60" ry="26" fill="#111827" />
          {/* Nostrils */}
          <ellipse cx="225" cy="340" rx="7" ry="10" fill="#030712" />
          <ellipse cx="275" cy="340" rx="7" ry="10" fill="#030712" />

          {/* NOSE RING — gold */}
          <ellipse cx="250" cy="372" rx="16" ry="6" fill="none" stroke="url(#bull-gold)" strokeWidth="3.5" />

          {/* EYES — glowing */}
          <g filter="url(#soft-glow)">
            <circle cx="208" cy="225" r="14" fill="url(#eye-glow)" />
            <circle cx="292" cy="225" r="14" fill="url(#eye-glow)" />
            <circle cx="208" cy="225" r="5" fill="#0A1128" />
            <circle cx="292" cy="225" r="5" fill="#0A1128" />
            <circle cx="210" cy="222" r="1.8" fill="#fff" />
            <circle cx="294" cy="222" r="1.8" fill="#fff" />
          </g>

          {/* Forehead "star" mark — finance accent */}
          <path
            d="M 250 135 L 256 148 L 270 150 L 260 160 L 263 174 L 250 168 L 237 174 L 240 160 L 230 150 L 244 148 Z"
            fill="url(#bull-gold)"
            opacity="0.9"
          />

          {/* Cheekbone highlights */}
          <path d="M 180 245 Q 200 280 175 295" stroke="#0EA5E9" strokeWidth="2" fill="none" opacity="0.5" />
          <path d="M 320 245 Q 300 280 325 295" stroke="#0EA5E9" strokeWidth="2" fill="none" opacity="0.5" />

          {/* Data-line overlay (subtle chart) inside head */}
          <polyline
            points="190,290 210,278 225,285 245,265 265,272 285,258 305,265 315,255"
            fill="none"
            stroke="#7DD3FC"
            strokeWidth="1.6"
            opacity="0.55"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Outer hair tufts */}
          <path d="M 145 160 L 135 125 L 165 145 Z" fill="url(#bull-gold)" opacity="0.85" />
          <path d="M 355 160 L 365 125 L 335 145 Z" fill="url(#bull-gold)" opacity="0.85" />

          {/* Ear tips */}
          <ellipse cx="158" cy="200" rx="20" ry="10" transform="rotate(-25 158 200)" fill="#0EA5E9" opacity="0.7" />
          <ellipse cx="342" cy="200" rx="20" ry="10" transform="rotate(25 342 200)" fill="#0EA5E9" opacity="0.7" />
        </svg>
      </motion.div>

      {/* Rotating ring under bull */}
      <motion.div
        className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[60%] h-6 rounded-[50%] border border-sky-500/30"
        style={{ background: "radial-gradient(ellipse at center, rgba(14,165,233,0.35), transparent 70%)" }}
        animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.div>
  );
}
